import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Eye, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { categories, products } from '../data/products';
import { useNavigate } from 'react-router-dom';
import '../styles/FireSafetyProducts.css';

const ITEMS_PER_PAGE = 9;

const FireSafetyProducts = () => {
    const navigate = useNavigate();
    const [currentView, setCurrentView] = useState('categories');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
        setCurrentView('products');
        setSearchQuery('');
        setCurrentPage(1);
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setCurrentView('details');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBack = () => {
        if (currentView === 'details') {
            setCurrentView('products');
        } else if (currentView === 'products') {
            setCurrentView('categories');
            setSelectedCategory(null);
            setSearchQuery('');
            setCurrentPage(1);
        }
    };

    useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedCategory]);

    const filteredCategories = useMemo(() => {
        if (!searchQuery.trim()) return categories;
        const q = searchQuery.toLowerCase();
        return categories.filter(c => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q));
    }, [searchQuery]);

    const filteredProducts = useMemo(() => {
        if (!selectedCategory) return [];
        const list = products[selectedCategory.id] || [];
        if (!searchQuery.trim()) return list;
        const q = searchQuery.toLowerCase();
        return list.filter(p => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }, [selectedCategory, searchQuery]);

    const paginatedCategories = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredCategories.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredCategories, currentPage]);

    const paginatedProducts = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredProducts, currentPage]);

    const totalPagesCategories = Math.ceil(filteredCategories.length / ITEMS_PER_PAGE) || 1;
    const totalPagesProducts = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE) || 1;

    const Pagination = ({ current, total, onChange }) => {
        if (total <= 1) return null;
        const pages = [];
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) pages.push(i);
            else if (pages[pages.length - 1] !== '...') pages.push('...');
        }
        return (
            <div className="pagination" role="navigation" aria-label="Paginação">
                <button className="page-btn" onClick={() => onChange(current - 1)} disabled={current === 1} aria-label="Página anterior"><ChevronLeft size={16} /></button>
                {pages.map((p, idx) => p === '...' ? <span key={`e-${idx}`} className="ellipsis">...</span> : (
                    <button key={p} className={`page-btn ${p === current ? 'active' : ''}`} onClick={() => onChange(p)} aria-current={p === current ? 'page' : undefined} aria-label={`Ir para página ${p}`}>{p}</button>
                ))}
                <button className="page-btn" onClick={() => onChange(current + 1)} disabled={current === total} aria-label="Próxima página"><ChevronRight size={16} /></button>
            </div>
        );
    };

    const CategoryCard = ({ category, index }) => (
        <div
            className="category-card"
            onClick={() => handleCategoryClick(category)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCategoryClick(category); } }}
            aria-label={`Ver produtos da categoria ${category.name}`}
            style={{ animationDelay: `${index * 0.06}s` }}
        >
            <div className="category-image">
                <img
                    src={category.image}
                    alt={category.name}
                    loading="lazy"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Imagem+Indisponível'; }}
                />
            </div>
            <h3>{category.name}</h3>
            <p>{category.description}</p>
        </div>
    );

    const ProductCard = ({ product, index }) => (
        <div
            className="product-card"
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => handleProductClick(product)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleProductClick(product); } }}
            aria-label={`Ver detalhes do produto ${product.name}`}
        >
            <div className="product-image">
                <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Imagem+Indisponível'; }}
                />
                <div className="product-overlay" aria-hidden="true">
                    <span
                        className="view-details-btn"
                        onClick={(e) => { e.stopPropagation(); handleProductClick(product); }}
                        role="button"
                        tabIndex={0}
                    >
                        <Eye size={18} aria-hidden="true" />
                        Ver Detalhes
                    </span>
                </div>
            </div>
            <div className="product-info">
                <h3>{product.name}</h3>
                <p>{product.description}</p>
            </div>
        </div>
    );

    const ProductDetails = ({ product }) => (
        <div className="product-details">
            <div className="product-details-grid">
                <div className="product-image-large">
                    <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Imagem+Indisponível'; }}
                    />
                </div>
                <div className="product-info-detailed">
                    <h1>{product.name}</h1>
                    <div className="price-large">{product.price || ''}</div>
                    <p className="description">{product.description}</p>
                    <div className="specifications">
                        <h3>Especificações Técnicas</h3>
                        {Object.entries(product.specifications || {}).map(([key, value]) => (
                            <div key={key} className="spec-item">
                                <span className="spec-label">{key.replaceAll('_', ' ').toUpperCase()}:</span>
                                <span className="spec-value">{value}</span>
                            </div>
                        ))}
                    </div>
                    <div className="features">
                        <h3>Características</h3>
                        <ul>
                            {(product.features || []).map((feature, index) => (
                                <li key={index}>{feature}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="action-buttons">
                        <button className="btn-primary" onClick={() => navigate('/contatos')} aria-label="Solicitar orçamento">Solicitar Orçamento</button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="fire-safety-app">
            <header className="header" aria-label="Cabeçalho da página de produtos">
                <div className="container header-content">
                    {currentView !== 'categories' && (
                        <button className="back-btn" onClick={handleBack} aria-label="Voltar">
                            <ArrowLeft size={18} aria-hidden="true" /> Voltar
                        </button>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h1>Produtos</h1>
                        <div className="breadcrumb">
                            {currentView === 'categories' && 'Categorias de Produtos'}
                            {currentView === 'products' && `${selectedCategory?.name} - Produtos`}
                            {currentView === 'details' && `${selectedCategory?.name} - ${selectedProduct?.name}`}
                        </div>
                    </div>
                </div>
            </header>

            <div className="container-produtos">
                {currentView === 'categories' && (
                    <>
                        <div className="products-toolbar">
                            <div className="search-box" role="search">
                                <Search size={18} aria-hidden="true" />
                                <input
                                    type="search"
                                    placeholder="Pesquisar categoria..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    aria-label="Pesquisar categorias"
                                />
                                {searchQuery && <button onClick={() => setSearchQuery('')} aria-label="Limpar pesquisa" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}><X size={16} /></button>}
                            </div>
                            <span className="results-count">{filteredCategories.length} categoria(s) encontrada(s)</span>
                        </div>
                        {paginatedCategories.length === 0 ? (
                            <p style={{ textAlign: 'center', marginTop: '2rem', color: 'var(--text-muted)' }}>Nenhuma categoria encontrada para "{searchQuery}".</p>
                        ) : (
                            <>
                                <div className="categories-grid">
                                    {paginatedCategories.map((category, index) => (
                                        <CategoryCard key={category.id} category={category} index={index} />
                                    ))}
                                </div>
                                <Pagination current={currentPage} total={totalPagesCategories} onChange={setCurrentPage} />
                            </>
                        )}
                    </>
                )}

                {currentView === 'products' && selectedCategory && (
                    <>
                        <div className="products-toolbar">
                            <div className="search-box" role="search">
                                <Search size={18} aria-hidden="true" />
                                <input
                                    type="search"
                                    placeholder={`Pesquisar em ${selectedCategory.name}...`}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    aria-label="Pesquisar produtos"
                                />
                                {searchQuery && <button onClick={() => setSearchQuery('')} aria-label="Limpar pesquisa" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}><X size={16} /></button>}
                            </div>
                            <span className="results-count">{filteredProducts.length} produto(s)</span>
                        </div>
                        {paginatedProducts.length === 0 ? (
                            <p style={{ textAlign: 'center', marginTop: '2rem', color: 'var(--text-muted)' }}>Nenhum produto encontrado para "{searchQuery}".</p>
                        ) : (
                            <>
                                <div className="products-grid">
                                    {paginatedProducts.map((product, index) => (
                                        <ProductCard key={product.id} product={product} index={index} />
                                    ))}
                                </div>
                                <Pagination current={currentPage} total={totalPagesProducts} onChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                            </>
                        )}
                    </>
                )}

                {currentView === 'details' && selectedProduct && (
                    <ProductDetails product={selectedProduct} />
                )}
            </div>
        </div>
    );
};

export default FireSafetyProducts;
